import { X, UserCheck, User, Mail, Phone, MapPin, CreditCard, Calendar, CheckCircle, Clock } from "lucide-react";

const getStatusBadge = (client) => {
  const status = client.status?.toLowerCase() || "inactive";
  if (status === "active") {
    return (
      <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-800 bg-green-100 rounded-full">
        <CheckCircle className="mr-1 w-3 h-3" />
        Active
      </span>
    );
  } else if (status === "pending") {
    return (
      <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-yellow-800 bg-yellow-100 rounded-full">
        <Clock className="mr-1 w-3 h-3" />
        Pending
      </span>
    );
  } else {
    return (
      <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-800 bg-gray-100 rounded-full">
        <Clock className="mr-1 w-3 h-3" />
        Inactive
      </span>
    );
  }
};

const ClientViewModal = ({ client, onClose }) => {
  const formatDate = (dateString) => {
    if (!dateString) return "";
    if (dateString.length === 8) {
      const day = dateString.slice(0, 2);
      const month = dateString.slice(2, 4);
      const year = dateString.slice(4, 8);
      return new Date(year, month - 1, day).toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    }
    return new Date(dateString).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <div className="flex fixed inset-0 z-50 justify-center items-center p-4">
      <div
        className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-5 bg-gradient-to-r from-emerald-600 to-blue-600">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <div className="flex justify-center items-center w-14 h-14 bg-white bg-opacity-20 rounded-full border-2 border-white border-opacity-30 backdrop-blur-sm">
                <UserCheck className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white">
                  {client.firstName} {client.lastName}
                </h3>
                <p className="text-emerald-100 text-sm mt-0.5">Client Information</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-white rounded-lg transition-all duration-200 hover:text-emerald-100 hover:bg-white hover:bg-opacity-20"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 p-6 bg-gray-50">
          <div className="space-y-6">
            {/* Personal Information */}
            <div className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200 shadow-sm">
              <div className="flex items-center mb-5">
                <div className="p-2 mr-3 bg-blue-100 rounded-lg">
                  <User className="w-5 h-5 text-blue-600" />
                </div>
                <h4 className="text-lg font-bold text-gray-900">Personal Information</h4>
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="p-4 bg-white rounded-lg border border-blue-100">
                  <label className="block mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
                    Full Name
                  </label>
                  <p className="text-base font-semibold text-gray-900">
                    {client.firstName} {client.lastName}
                  </p>
                </div>
                {client.gender && (
                  <div className="p-4 bg-white rounded-lg border border-blue-100">
                    <label className="block mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
                      Gender
                    </label>
                    <p className="text-base font-medium text-gray-900 capitalize">
                      {client.gender}
                    </p>
                  </div>
                )}
                {client.dob && (
                  <div className="p-4 bg-white rounded-lg border border-blue-100">
                    <label className="block mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
                      Date of Birth
                    </label>
                    <div className="flex items-center">
                      <Calendar className="mr-2 w-4 h-4 text-gray-400" />
                      <p className="text-base font-medium text-gray-900">
                        {formatDate(client.dob)}
                      </p>
                    </div>
                  </div>
                )}
                <div className="p-4 bg-white rounded-lg border border-blue-100">
                  <label className="block mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
                    Account Status
                  </label>
                  <div className="mt-1">{getStatusBadge(client)}</div>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="p-6 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border border-purple-200 shadow-sm">
              <div className="flex items-center mb-5">
                <div className="p-2 mr-3 bg-purple-100 rounded-lg">
                  <Phone className="w-5 h-5 text-purple-600" />
                </div>
                <h4 className="text-lg font-bold text-gray-900">Contact Information</h4>
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {client.email && (
                  <div className="p-4 bg-white rounded-lg border border-purple-100">
                    <label className="block mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
                      Email Address
                    </label>
                    <div className="flex items-center">
                      <Mail className="flex-shrink-0 mr-2 w-4 h-4 text-gray-400" />
                      <p className="text-sm font-medium text-gray-900 break-all">
                        {client.email}
                      </p>
                    </div>
                  </div>
                )}
                {client.phone && (
                  <div className="p-4 bg-white rounded-lg border border-purple-100">
                    <label className="block mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
                      Phone Number
                    </label>
                    <div className="flex items-center">
                      <Phone className="flex-shrink-0 mr-2 w-4 h-4 text-gray-400" />
                      <p className="text-sm font-medium text-gray-900">
                        {client.phone}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Location Information */}
            {(client.location || client.region) && (
              <div className="p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-200 shadow-sm">
                <div className="flex items-center mb-5">
                  <div className="p-2 mr-3 bg-green-100 rounded-lg">
                    <MapPin className="w-5 h-5 text-green-600" />
                  </div>
                  <h4 className="text-lg font-bold text-gray-900">Location Information</h4>
                </div>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {client.location && (
                    <div className="p-4 bg-white rounded-lg border border-green-100">
                      <label className="block mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
                        Location
                      </label>
                      <div className="flex items-center">
                        <MapPin className="flex-shrink-0 mr-2 w-4 h-4 text-gray-400" />
                        <p className="text-sm font-medium text-gray-900">
                          {client.location}
                        </p>
                      </div>
                    </div>
                  )}
                  {client.region && (
                    <div className="p-4 bg-white rounded-lg border border-green-100">
                      <label className="block mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
                        Region
                      </label>
                      <p className="text-sm font-medium text-gray-900">
                        {client.region}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ID Document Information */}
            {client.documentType && client.documentType !== "N/A" && (
              <div className="p-6 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border border-amber-200 shadow-sm">
                <div className="flex items-center mb-5">
                  <div className="p-2 mr-3 bg-amber-100 rounded-lg">
                    <CreditCard className="w-5 h-5 text-amber-600" />
                  </div>
                  <h4 className="text-lg font-bold text-gray-900">ID Document Information</h4>
                </div>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="p-4 bg-white rounded-lg border border-amber-100">
                    <label className="block mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
                      Document Type
                    </label>
                    <p className="text-sm font-semibold text-gray-900">
                      {client.documentType}
                    </p>
                  </div>
                  {client.cardNumber && (
                    <div className="p-4 bg-white rounded-lg border border-amber-100">
                      <label className="block mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
                        Card Number
                      </label>
                      <span className="text-sm font-mono bg-gray-100 text-gray-800 px-3 py-1.5 rounded-lg inline-block">
                        {client.cardNumber}
                      </span>
                    </div>
                  )}
                  {client.issueDate && (
                    <div className="p-4 bg-white rounded-lg border border-amber-100">
                      <label className="block mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
                        Issue Date
                      </label>
                      <div className="flex items-center">
                        <Calendar className="flex-shrink-0 mr-2 w-4 h-4 text-gray-400" />
                        <p className="text-sm font-medium text-gray-900">
                          {formatDate(client.issueDate)}
                        </p>
                      </div>
                    </div>
                  )}
                  {client.expiryDate && (
                    <div className="p-4 bg-white rounded-lg border border-amber-100">
                      <label className="block mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
                        Expiry Date
                      </label>
                      <div className="flex items-center">
                        <Calendar className="flex-shrink-0 mr-2 w-4 h-4 text-gray-400" />
                        <p className="text-sm font-medium text-gray-900">
                          {formatDate(client.expiryDate)}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                {(client.idCardFront || client.idCardBack) && (
                  <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                    {client.idCardFront && (
                      <div className="p-4 bg-white rounded-lg border border-amber-100">
                        <label className="block mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
                          ID Card Front
                        </label>
                        <img
                          src={client.idCardFront}
                          alt="ID card front"
                          className="w-full h-48 object-cover rounded-lg border border-gray-200"
                        />
                      </div>
                    )}
                    {client.idCardBack && (
                      <div className="p-4 bg-white rounded-lg border border-amber-100">
                        <label className="block mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
                          ID Card Back
                        </label>
                        <img
                          src={client.idCardBack}
                          alt="ID card back"
                          className="w-full h-48 object-cover rounded-lg border border-gray-200"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Agent Information */}
            {client.agent && (
              <div className="p-6 bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl border border-indigo-200 shadow-sm">
                <div className="flex items-center mb-5">
                  <div className="p-2 mr-3 bg-indigo-100 rounded-lg">
                    <User className="w-5 h-5 text-indigo-600" />
                  </div>
                  <h4 className="text-lg font-bold text-gray-900">Registering Agent</h4>
                </div>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {client.agentName && (
                    <div className="p-4 bg-white rounded-lg border border-indigo-100">
                      <label className="block mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
                        Agent Name
                      </label>
                      <p className="text-sm font-semibold text-gray-900">
                        {client.agentName}
                      </p>
                    </div>
                  )}
                  {client.agentCode && (
                    <div className="p-4 bg-white rounded-lg border border-indigo-100">
                      <label className="block mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
                        Agent Code
                      </label>
                      <span className="text-sm font-mono bg-emerald-100 text-emerald-800 px-3 py-1.5 rounded-lg inline-block font-semibold">
                        {client.agentCode}
                      </span>
                    </div>
                  )}
                  {client.agentEmail && (
                    <div className="p-4 bg-white rounded-lg border border-indigo-100">
                      <label className="block mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
                        Agent Email
                      </label>
                      <div className="flex items-center">
                        <Mail className="flex-shrink-0 mr-2 w-4 h-4 text-gray-400" />
                        <p className="text-sm font-medium text-gray-900 break-all">
                          {client.agentEmail}
                        </p>
                      </div>
                    </div>
                  )}
                  {client.agentPhone && (
                    <div className="p-4 bg-white rounded-lg border border-indigo-100">
                      <label className="block mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
                        Agent Phone
                      </label>
                      <div className="flex items-center">
                        <Phone className="flex-shrink-0 mr-2 w-4 h-4 text-gray-400" />
                        <p className="text-sm font-medium text-gray-900">
                          {client.agentPhone}
                        </p>
                      </div>
                    </div>
                  )}
                  {client.agentLocation && (
                    <div className="p-4 bg-white rounded-lg border border-indigo-100">
                      <label className="block mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
                        Agent Location
                      </label>
                      <div className="flex items-center">
                        <MapPin className="flex-shrink-0 mr-2 w-4 h-4 text-gray-400" />
                        <p className="text-sm font-medium text-gray-900">
                          {client.agentLocation}
                        </p>
                      </div>
                    </div>
                  )}
                  {client.agentRegion && (
                    <div className="p-4 bg-white rounded-lg border border-indigo-100">
                      <label className="block mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
                        Agent Region
                      </label>
                      <p className="text-sm font-medium text-gray-900">
                        {client.agentRegion}
                      </p>
                    </div>
                  )}
                  {client.agentType && (
                    <div className="p-4 bg-white rounded-lg border border-indigo-100">
                      <label className="block mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
                        Agent Type
                      </label>
                      <span className={`inline-block px-3 py-1 text-xs font-semibold rounded-lg ${
                        client.agentType === "direct" 
                          ? "bg-blue-100 text-blue-800" 
                          : "bg-purple-100 text-purple-800"
                      }`}>
                        {client.agentType.charAt(0).toUpperCase() + client.agentType.slice(1)}
                      </span>
                    </div>
                  )}
                  {client.agentStatus !== null && (
                    <div className="p-4 bg-white rounded-lg border border-indigo-100">
                      <label className="block mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
                        Agent Status
                      </label>
                      <span className={`inline-flex items-center px-3 py-1 text-xs font-medium rounded-full ${
                        client.agentStatus === 1
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-100 text-gray-800"
                      }`}>
                        {client.agentStatus === 1 ? "Active" : "Inactive"}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end px-6 py-4 bg-white border-t border-gray-200 shadow-inner">
          <button
            onClick={onClose}
            className="px-8 py-2.5 bg-gradient-to-r from-emerald-600 to-blue-600 text-white rounded-lg hover:from-emerald-700 hover:to-blue-700 transition-all duration-200 font-semibold shadow-md hover:shadow-lg"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClientViewModal;
